import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../api-response.model';

// ─── Envelope helpers ──────────────────────────────────────────────────────────

/**
 * The envelope is camelCase but the payloads inside it come back PascalCase from .NET
 * (`ProviderCode`, `IsResolved`, `CreatedAtUtc`). Lower-casing the first character of every
 * key gives us one consistent shape to model against, and leaves an already-camelCase
 * payload untouched.
 *
 * Caveat: this also rewrites dictionary keys, so a payload whose *keys are data* (an
 * all-caps provider map, say) would be altered. None of the endpoints here return one.
 */
function camelizeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelizeKeys);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      out[key ? key.charAt(0).toLowerCase() + key.slice(1) : key] = camelizeKeys(inner);
    }
    return out;
  }
  return value;
}

/**
 * Unwraps the ApiResponse envelope. Some routes return the payload directly rather than
 * wrapped, so fall back to the response itself when there is no `data` key.
 */
function unwrap<T>(obs: Observable<ApiResponse<T>>): Observable<T> {
  return obs.pipe(
    map(r => (r && typeof r === 'object' && 'data' in r ? r.data : (r as unknown as T))),
    map(data => camelizeKeys(data) as T)
  );
}

/** List endpoints have been seen returning a bare array and a wrapped list. Accept both. */
function normaliseList<T>(
  data: unknown
): T[] {
  const list = data as T[] | { items?: T[]; connections?: T[]; results?: T[] } | null | undefined;
  if (!list) return [];
  if (Array.isArray(list)) return list;
  return list.items ?? list.connections ?? list.results ?? [];
}

// ─── Control overview ─────────────────────────────────────────────────────────

export interface XeroOverviewStatus {
  isConnected: boolean;
  orgName: string | null;
  connectionStatus: string | null;
  lastConnectedAt: string | null;
  lastSyncAt: string | null;
  hasConfiguration: boolean;
  activeJobs: number;
}

export interface EvertimeOverviewStatus {
  isConnected: boolean;
  isConfigured: boolean;
  connectionStatus: string | null;
  lastConnectedAt: string | null;
  lastSyncAt: string | null;
}

export interface IntegrationControlOverview {
  xero: XeroOverviewStatus;
  evertime: EvertimeOverviewStatus;
  unresolvedErrors: number;
}

// ─── Scheduler settings ───────────────────────────────────────────────────────

export interface TenantScheduleSettings {
  providerCode: string;
  isEnabled: boolean;
  autoSyncEnabled: boolean;
  syncIntervalMinutes: number;
  liveSyncEnabled: boolean;
  settingsJson: string | null;
}

export interface SaveScheduleRequest {
  isEnabled: boolean;
  autoSyncEnabled: boolean;
  syncIntervalMinutes: number;
  liveSyncEnabled: boolean;
  settingsJson?: string | null;
}

// ─── Xero configuration ───────────────────────────────────────────────────────

export interface XeroApplicationSummary {
  providerApplicationId: number;
  applicationCode: string;
  environmentCode: string;
  clientId: string;
  redirectUri: string;
  scopes: string;
  hasClientSecret: boolean;
  isDefault: boolean;
  isActive: boolean;
  clientSecretLastRotatedAtUtc: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface XeroConfiguration {
  applications: XeroApplicationSummary[];
  defaultApplication: XeroApplicationSummary | null;
  isConfigured: boolean;
}

export interface XeroApplicationRequest {
  applicationCode: string;
  environmentCode: string;
  clientId: string;
  /**
   * Required on POST (create) — the backend rejects a create without it. Must be null on
   * PUT (metadata update); rotations go through updateXeroCredentials() instead.
   */
  clientSecret: string | null;
  redirectUri: string;
  scopes: string;
  isDefault: boolean;
  isActive: boolean;
}

/**
 * GET .../applications/current returns either the full application or a "not configured"
 * marker, so every field beyond `configured` is optional.
 */
export interface XeroCurrentApplication {
  configured?: boolean;
  providerApplicationId?: number;
  applicationCode?: string;
  environmentCode?: string;
  clientId?: string;
  redirectUri?: string;
  scopes?: string;
  hasClientSecret?: boolean;
  isDefault?: boolean;
  isActive?: boolean;
  clientSecretLastRotatedAtUtc?: string | null;
  createdAtUtc?: string;
  updatedAtUtc?: string;
}

/** Used when updating credentials on an existing application — sends only what changed. */
export interface XeroCredentialsUpdateRequest {
  environmentCode: string;
  clientId: string;
  clientSecret: string;
}

/** Legacy/direct sync configuration request for POST /api/integrations/xero/configuration/sync */
export interface XeroSyncConfigurationRequest {
  isEnabled: boolean;
  intervalMinutes: number;
  runContacts: boolean;
  runAccounts: boolean;
  runInvoices: boolean;
  runManualJournals: boolean;
  runBankTransactions: boolean;
  maximumConcurrentRequests?: number;
  minimumDelayMilliseconds?: number;
  maximumRetryAttempts?: number;
}

// ─── Xero OAuth ───────────────────────────────────────────────────────────────

export interface XeroConnectUrlResponse {
  authorizationUrl: string;
  expiresInMinutes: number;
  redirectUri: string;
  returnUrl?: string | null;
}

// ─── Readiness ────────────────────────────────────────────────────────────────

/** Per-provider readiness. Field set is tolerant — not every deployment returns every key. */
export interface ProviderReadiness {
  isConfigured?: boolean;
  isConnected?: boolean;
  readyToSync?: boolean;
  reconnectRequired?: boolean;
  connectionStatus?: string | null;
  lastSyncAtUtc?: string | null;
  retryAfterSeconds?: number | null;
  issues?: string[] | null;
  message?: string | null;
}

export interface IntegrationReadinessResponse {
  xero?: ProviderReadiness;
  evertime?: ProviderReadiness;
  dashboardAccessible?: boolean;
}

// ─── Xero connections (connected organisations) ───────────────────────────────

/** A Xero organisation the tenant has authorised. Field set is tolerant — the backend
 *  has grown this shape over time and not every deployment returns every key. */
export interface XeroConnection {
  connectionId?: string;
  connectionStatus?: string | null;
  reconnectRequired?: boolean;
  tenantId?: string;
  tenantName?: string;
  tenantType?: string | null;
  status?: string | null;
  isActive?: boolean;
  authEventId?: string | null;
  createdAtUtc?: string | null;
  updatedAtUtc?: string | null;
  lastSyncAtUtc?: string | null;
  expiresAtUtc?: string | null;
}

export interface XeroConfigurationStatusResponse {
  isConfigured: boolean;
  applicationCode: string | null;
  clientId: string | null;
  hasClientSecret: boolean;
  canConnect: boolean;
  issues: string[];
}

// ─── Xero sync status ─────────────────────────────────────────────────────────

export interface XeroSyncJob {
  jobId: string;
  entityCode: string;
  syncMode: string;
  jobStatus: string;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  totalDurationMs: number | null;
  recordsReceived: number;
  recordsInserted: number;
  recordsUpdated: number;
  recordsUnchanged: number;
  recordsFailed: number;
  attemptCount: number;
  apiCallCount: number;
  lastErrorCode: string | null;
  lastError: string | null;
}

export interface XeroSyncStatusResponse {
  compId: number;
  serverTimeUtc: string;
  tenantSchedule: XeroTenantSchedule;
  timer: XeroSyncTimer;
  counts: Record<string, number>;
  diagnostics: XeroSyncDiagnosticsSummary;
  rateLimitHealth: XeroRateLimitHealth;
  latestJobs: XeroSyncJob[];
  recentErrors: XeroSyncErrorItem[];
  entityHealth: XeroEntityHealth[];
  connectionHealth: XeroConnectionHealth[];
  alerts: unknown[];
}

export interface XeroSyncErrorItem {
  apiRequestId: number;
  jobId: string;
  endpoint: string;
  startedAtUtc: string;
  httpStatusCode: number;
  retryAfterSeconds: number | null;
  rateLimitProblem: string | null;
  errorMessage: string | null;
}

export interface XeroTenantSchedule {
  configured: boolean;
  isEnabled: boolean;
  autoSyncEnabled: boolean;
  liveSyncEnabled: boolean;
  effectiveIntervalMinutes: number;
  hasActiveConnection: boolean;
  hasActiveWork: boolean;
  lastSuccessfulSyncAtUtc: string | null;
  nextAutoSyncAtUtc: string | null;
  secondsUntilNextAutoSync: number | null;
  scheduleStatus: string;
}

export interface XeroSyncTimer {
  kind: string;
  enabled: boolean;
  intervalMinutes: number;
  lastTriggeredAtUtc: string | null;
  lastCompletedAtUtc: string | null;
  lastError: string | null;
}

export interface XeroSyncDiagnosticsSummary {
  windowHours: number;
  apiCallsLast24Hours: number;
  successfulApiCallsLast24Hours: number;
  failedApiCallsLast24Hours: number;
  rateLimit429CountLast24Hours: number;
  activeJobCount: number;
  lastSuccessfulJobAtUtc: string | null;
  lastFailedJobAtUtc: string | null;
}

export interface XeroRateLimitHealth {
  minuteRemaining: number;
  dailyRemaining: number;
  appMinuteRemaining: number;
  sampledAtUtc: string | null;
  last429AtUtc: string | null;
}

export interface XeroEntityHealth {
  entityCode: string;
  initialSyncCompleted: boolean;
  lastSuccessfulSyncAtUtc: string | null;
  nextIncrementalSyncAtUtc: string | null;
  nextRunDueInSeconds: number | null;
  consecutiveFailures: number;
  lastError: string | null;
}

export interface XeroConnectionHealth {
  connectionStatus: string;
  grantStatus: string;
  lastConnectedAtUtc: string | null;
  lastSuccessfulSyncAtUtc: string | null;
  accessTokenExpiresAtUtc: string | null;
  reconnectRequired: boolean;
  secretRotationSuggested: boolean;
  permissionReviewSuggested: boolean;
  lastRefreshError: string | null;
}

export interface XeroSyncNowRequest {
  syncMode: 'Incremental' | 'Initial' | 'FullReconciliation';
  entities: string[];
}

export interface XeroSyncNowResponse {
  accepted: boolean;
  jobCount: number;
  requestedAtUtc: string;
}

// ─── Xero sync requests & diagnostics ────────────────────────────────────────

export interface XeroSyncRequestItem {
  apiRequestId: number;
  jobId: string;
  batchId: string | null;
  connectionId: string;
  httpMethod: string;
  endpoint: string;
  startedAtUtc: string;
  completedAtUtc: string | null;
  durationMs: number | null;
  throttleWaitDurationMs: number | null;
  httpStatusCode: number | null;
  retryAfterSeconds: number | null;
  minuteLimitRemaining: number | null;
  appMinuteLimitRemaining: number | null;
  dailyLimitRemaining: number | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface XeroSyncRequestsResponse {
  count: number;
  items: XeroSyncRequestItem[];
}

export interface XeroSyncDiagnosticsResponse {
  windowHours: number;
  totals: Record<string, unknown>;
  latest429: unknown[];
  duplicateActiveJobs: unknown[];
  suspiciousDuplicateCalls: unknown[];
  retryGroups: unknown[];
  zeroTableExplanation: unknown[];
}

// ─── Evertime settings ────────────────────────────────────────────────────────

export interface EvertimeSettingsResponse {
  baseUrl: string | null;
  apiKeyHeaderName: string | null;
  timeoutSeconds: number;
  batchSize: number;
  isEnabled: boolean;
  hasApiKey: boolean;
  compId: number;
}

/**
 * Upsert — the whole settings object is replaced, so always send every field, not just the
 * ones that changed. `apiKey: null` means "keep the stored key"; a string rotates it.
 */
export interface SaveEvertimeSettingsRequest {
  baseUrl?: string | null;
  apiKey?: string | null;
  apiKeyHeaderName?: string | null;
  timeoutSeconds?: number;
  batchSize?: number;
  isEnabled: boolean;
}

// ─── Evertime request log ─────────────────────────────────────────────────────

/** One outbound HTTP call the backend made to Evertime. Tolerant field set. */
export interface EvertimeRequestItem {
  apiRequestId?: number;
  runId?: string | null;
  httpMethod?: string | null;
  endpoint?: string | null;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
  durationMs?: number | null;
  httpStatusCode?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

// ─── Evertime status / history ────────────────────────────────────────────────

export interface EvertimeSyncRun {
  runId: string;
  status: string;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  recordsProcessed: number;
  errorMessage: string | null;
  syncType: string;
}

export interface EvertimeStatusResponse {
  isConnected: boolean;
  isConfigured: boolean;
  lastSyncAt: string | null;
  nextScheduledAt: string | null;
  connectionStatus: string | null;
}

// ─── Combined errors ──────────────────────────────────────────────────────────

export interface IntegrationError {
  integrationErrorLogId?: number;
  /** Older shape used a string id; keep both so either serialisation renders. */
  errorId?: string;
  compId?: number | null;
  providerCode: string;
  connectionId?: string | null;
  jobId?: string | null;
  runId?: string | null;
  severity?: string | null;
  errorCode: string | null;
  stage: string;
  message: string;
  /** Full exception text and stack trace — can be tens of kilobytes. */
  detail?: string | null;
  correlationId: string | null;
  traceId?: string | null;
  isResolved: boolean;
  resolvedAtUtc?: string | null;
  createdAtUtc: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class IntegrationControlService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/integrations`;

  // ── Control overview ────────────────────────────────────────────────────────

  getOverview(): Observable<IntegrationControlOverview> {
    return unwrap(this.http.get<ApiResponse<IntegrationControlOverview>>(`${this.base}/control`));
  }

  /**
   * GET /api/integrations/control/readiness — Xero, Evertime and dashboard readiness,
   * reported independently so a broken integration never gates the dashboard.
   */
  getReadiness(): Observable<IntegrationReadinessResponse> {
    return unwrap(this.http.get<ApiResponse<IntegrationReadinessResponse>>(`${this.base}/control/readiness`));
  }

  // ── Schedule settings ───────────────────────────────────────────────────────

  getSettings(providerCode?: 'XERO' | 'EVERTIME'): Observable<TenantScheduleSettings[]> {
    let params = new HttpParams();
    if (providerCode) params = params.set('providerCode', providerCode);
    return unwrap(this.http.get<ApiResponse<TenantScheduleSettings[]>>(`${this.base}/control/settings`, { params }));
  }

  saveSchedule(providerCode: 'XERO' | 'EVERTIME', request: SaveScheduleRequest): Observable<unknown> {
    return unwrap(this.http.put<ApiResponse<unknown>>(`${this.base}/control/settings/${providerCode}`, request));
  }

  // ── Xero configuration ──────────────────────────────────────────────────────

  getXeroConfiguration(): Observable<XeroConfiguration> {
    return unwrap(this.http.get<ApiResponse<XeroConfiguration>>(`${this.base}/xero/configuration`));
  }

  /** Gets all Xero applications: GET /api/integrations/xero/configuration/applications */
  getXeroApplications(): Observable<XeroApplicationSummary[]> {
    return unwrap(this.http.get<ApiResponse<XeroApplicationSummary[]>>(`${this.base}/xero/configuration/applications`));
  }

  /** Gets the current active Xero application: GET /api/integrations/xero/configuration/applications/current */
  getCurrentXeroApplication(): Observable<XeroCurrentApplication> {
    return unwrap(this.http.get<ApiResponse<XeroCurrentApplication>>(`${this.base}/xero/configuration/applications/current`));
  }

  /** Creates a new Xero application: POST /api/integrations/xero/configuration/applications */
  createXeroApplication(request: XeroApplicationRequest): Observable<unknown> {
    return unwrap(this.http.post<ApiResponse<unknown>>(`${this.base}/xero/configuration/applications`, request));
  }

  /** Alias for createXeroApplication */
  saveXeroApplication(request: XeroApplicationRequest): Observable<unknown> {
    return this.createXeroApplication(request);
  }

  /** Updates an entire Xero application (full update): PUT /api/integrations/xero/configuration/applications */
  updateXeroApplication(request: XeroApplicationRequest): Observable<unknown> {
    return unwrap(this.http.put<ApiResponse<unknown>>(`${this.base}/xero/configuration/applications`, request));
  }

  /**
   * Updates only the credentials (clientId + clientSecret) for an application that already exists.
   * PUT /api/integrations/xero/configuration/applications/{applicationCode}/credentials
   */
  updateXeroCredentials(applicationCode: string, request: XeroCredentialsUpdateRequest): Observable<unknown> {
    return unwrap(
      this.http.put<ApiResponse<unknown>>(
        `${this.base}/xero/configuration/applications/${encodeURIComponent(applicationCode)}/credentials`,
        request
      )
    );
  }

  /** Saves Xero sync settings directly: POST /api/integrations/xero/configuration/sync */
  saveXeroSyncConfiguration(request: XeroSyncConfigurationRequest): Observable<unknown> {
    return unwrap(this.http.post<ApiResponse<unknown>>(`${this.base}/xero/configuration/sync`, request));
  }

  // ── Xero OAuth ──────────────────────────────────────────────────────────────

  getXeroConfigurationStatus(): Observable<XeroConfigurationStatusResponse> {
    return unwrap(this.http.get<ApiResponse<XeroConfigurationStatusResponse>>(`${this.base}/xero/oauth/configuration-status`));
  }

  /**
   * GET /api/integrations/xero/oauth/connect-url?applicationCode=…&returnUrl=…
   * `returnUrl` is where Xero's backend callback sends the browser once tokens are stored.
   */
  getXeroConnectUrl(applicationCode?: string, returnUrl?: string): Observable<XeroConnectUrlResponse> {
    let params = new HttpParams();
    if (applicationCode) params = params.set('applicationCode', applicationCode);
    if (returnUrl) params = params.set('returnUrl', returnUrl);
    return unwrap(this.http.get<ApiResponse<XeroConnectUrlResponse>>(`${this.base}/xero/oauth/connect-url`, { params }));
  }

  // ── Xero connections ────────────────────────────────────────────────────────

  /** GET /api/integrations/xero/connections?search=…&refresh=… */
  getXeroConnections(search?: string, refresh = false): Observable<XeroConnection[]> {
    let params = new HttpParams().set('refresh', refresh);
    if (search) params = params.set('search', search);
    return this.http
      .get<ApiResponse<XeroConnection[] | { items?: XeroConnection[]; connections?: XeroConnection[] }>>(
        `${this.base}/xero/connections`,
        { params }
      )
      .pipe(map(r => normaliseList<XeroConnection>(camelizeKeys(r.data))));
  }

  /** POST /api/integrations/xero/connections/refresh — asks the backend to re-pull from Xero. */
  refreshXeroConnections(): Observable<XeroConnection[]> {
    return this.http
      .post<ApiResponse<XeroConnection[] | { items?: XeroConnection[]; connections?: XeroConnection[] }>>(
        `${this.base}/xero/connections/refresh`,
        {}
      )
      .pipe(map(r => normaliseList<XeroConnection>(camelizeKeys(r.data))));
  }

  // ── Xero sync ───────────────────────────────────────────────────────────────

  /** Starts a user-requested incremental Xero sync. */
  triggerXeroManualSync(): Observable<unknown> {
    return this.http.post<unknown>(environment.xeroManualSyncApiUrl, {});
  }

  triggerXeroSync(mode: 'Incremental' | 'Initial' | 'FullReconciliation' = 'Incremental'): Observable<XeroSyncNowResponse> {
    const body: XeroSyncNowRequest = {
      syncMode: mode,
      entities: ['CONTACT', 'ACCOUNT', 'INVOICE', 'OVERPAYMENT', 'CREDIT_NOTE', 'MANUAL_JOURNAL', 'BANK_TRANSACTION'],
    };
    return unwrap(this.http.post<ApiResponse<XeroSyncNowResponse>>(`${this.base}/control/xero/sync-now`, body));
  }

  getXeroSyncStatus(take = 50): Observable<XeroSyncStatusResponse> {
    const params = new HttpParams().set('take', take);
    return unwrap(this.http.get<ApiResponse<XeroSyncStatusResponse>>(`${this.base}/xero/sync/status`, { params }));
  }

  /**
   * Triggers a full sync via the direct Xero sync controller (all entities, all records).
   * Use this for a one-off full re-sync; normal incremental syncs go through triggerXeroSync().
   */
  triggerXeroSyncAll(): Observable<XeroSyncNowResponse> {
    return unwrap(this.http.post<ApiResponse<XeroSyncNowResponse>>(`${this.base}/xero/sync/all`, {}));
  }

  /** Raw Xero API request log — shows every call made to Xero including 429 rate-limit hits. */
  getXeroSyncRequests(take = 200, errorsOnly = false): Observable<XeroSyncRequestsResponse> {
    const params = new HttpParams().set('take', take).set('errorsOnly', errorsOnly);
    return unwrap(this.http.get<ApiResponse<XeroSyncRequestsResponse>>(`${this.base}/xero/sync/requests`, { params }));
  }

  /** Xero sync diagnostics — rate-limit analysis, duplicate detection, retry groups. */
  getXeroSyncDiagnostics(): Observable<XeroSyncDiagnosticsResponse> {
    return unwrap(this.http.get<ApiResponse<XeroSyncDiagnosticsResponse>>(`${this.base}/xero/sync/diagnostics`));
  }

  // ── Evertime settings ───────────────────────────────────────────────────────

  getEvertimeSettings(compId?: number | null): Observable<EvertimeSettingsResponse> {
    let params = new HttpParams();
    if (compId != null) params = params.set('compId', compId);
    return unwrap(this.http.get<ApiResponse<EvertimeSettingsResponse>>(`${this.base}/evertime/settings`, { params }));
  }

  saveEvertimeSettings(request: SaveEvertimeSettingsRequest): Observable<unknown> {
    return unwrap(this.http.post<ApiResponse<unknown>>(`${this.base}/evertime/settings`, request));
  }

  // ── Evertime status / history ───────────────────────────────────────────────

  getEvertimeStatus(compId?: number | null): Observable<EvertimeStatusResponse> {
    let params = new HttpParams();
    if (compId != null) params = params.set('compId', compId);
    return unwrap(this.http.get<ApiResponse<EvertimeStatusResponse>>(`${this.base}/evertime/status`, { params }));
  }

  getEvertimeHistory(take = 50): Observable<EvertimeSyncRun[]> {
    const params = new HttpParams().set('take', take);
    return unwrap(this.http.get<ApiResponse<EvertimeSyncRun[]>>(`${this.base}/evertime/history`, { params }));
  }

  /** POST /api/integrations/control/evertime/sync-now?fromDate=&endDate=&invoiceStatus= */
  triggerEvertimeSync(fromDate?: string, endDate?: string, invoiceStatus?: number): Observable<unknown> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (endDate) params = params.set('endDate', endDate);
    if (invoiceStatus != null) params = params.set('invoiceStatus', invoiceStatus);
    return unwrap(this.http.post<ApiResponse<unknown>>(`${this.base}/control/evertime/sync-now`, {}, { params }));
  }

  /** GET /api/integrations/evertime/requests — outbound Evertime API call log. */
  getEvertimeRequests(take = 100, errorsOnly = false): Observable<EvertimeRequestItem[]> {
    const params = new HttpParams().set('take', take).set('errorsOnly', errorsOnly);
    return this.http
      .get<ApiResponse<EvertimeRequestItem[] | { items?: EvertimeRequestItem[] }>>(
        `${this.base}/evertime/requests`,
        { params }
      )
      .pipe(map(r => normaliseList<EvertimeRequestItem>(camelizeKeys(r.data))));
  }

  // ── Combined errors ─────────────────────────────────────────────────────────

  getErrors(providerCode?: 'XERO' | 'EVERTIME', take = 100, unresolvedOnly = false): Observable<IntegrationError[]> {
    let params = new HttpParams().set('take', take).set('unresolvedOnly', unresolvedOnly);
    if (providerCode) params = params.set('providerCode', providerCode);
    return unwrap(this.http.get<ApiResponse<IntegrationError[]>>(`${this.base}/control/errors`, { params }));
  }
}
